import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Remount the whole playback session on a new source/retry. ReactPlayer keeps
// readiness and its last progress sample when only its URL prop changes.
const PlaybackSession = ({
  Player,
  url,
  playerRef,
  playing,
  onProgress,
  onDuration,
  playbackRate,
  volume,
  config,
  onSourceChange,
  onPlaybackChange,
  onSeek,
  onFailure,
  onRetry,
  children,
}) => {
  const internalRef = useRef(null);
  const publishedRef = useRef(null);
  const active = useRef(true);
  const failureLatch = useRef(false);
  const metadataCleanup = useRef(null);
  const durationCallback = useRef(onDuration);
  durationCallback.current = onDuration;
  const [failed, setFailed] = useState(false);
  const clearPlayer = () => {
    metadataCleanup.current?.();
    metadataCleanup.current = null;
    if (playerRef.current === publishedRef.current) playerRef.current = null;
  };
  useLayoutEffect(() => {
    active.current = true;
    onSourceChange?.();
    return () => {
      active.current = false;
      metadataCleanup.current?.();
      metadataCleanup.current = null;
      if (playerRef.current === publishedRef.current) playerRef.current = null;
    };
  }, [playerRef, onSourceChange]);
  const ready = () => {
    if (!active.current || failureLatch.current || !url || !internalRef.current) return;
    publishedRef.current = internalRef.current;
    playerRef.current = internalRef.current;
    setFailed(false);
    if (!metadataCleanup.current) {
      const player = internalRef.current;
      const media = player.getInternalPlayer?.();
      const hls = player.getInternalPlayer?.("hls");
      const levelUpdated = hls?.constructor?.Events?.LEVEL_UPDATED;
      const update = () => {
        if (active.current && playerRef.current === player) durationCallback.current?.(player.getDuration?.());
      };
      // Paused ReactPlayer suppresses progress if time/buffer values are equal.
      // Metadata can still change: playlist ENDLIST, growing DVR, native duration.
      const mediaEvents = ["durationchange", "progress", "loadedmetadata"];
      const hlsEvents = [levelUpdated, hls?.constructor?.Events?.BUFFER_APPENDED].filter(Boolean);
      mediaEvents.forEach((event) => media?.addEventListener?.(event, update));
      hlsEvents.forEach((event) => hls.on?.(event, update));
      const timer = setInterval(update, 500);
      update();
      metadataCleanup.current = () => {
        clearInterval(timer);
        mediaEvents.forEach((event) => media?.removeEventListener?.(event, update));
        hlsEvents.forEach((event) => hls.off?.(event, update));
      };
    }
  };
  const isReady = () => active.current && internalRef.current && playerRef.current === internalRef.current;

  return (
    <>
      {failed && (
        <div className="video-url-error" role="alert" style={{ color: "#f87171" }}>
          影片載入失敗，請重試
          <button type="button" onClick={onRetry}>重試影片</button>
        </div>
      )}
      <div className="video-box">
        <Player
          className="rp"
          width="100%"
          height="100%"
          ref={internalRef}
          url={url}
          config={config}
          playing={playing}
          onReady={ready}
          onPlay={() => { ready(); if (isReady()) onPlaybackChange?.(true); }}
          onPause={() => { if (isReady()) onPlaybackChange?.(false); }}
          onEnded={() => { if (isReady()) onPlaybackChange?.(false); }}
          onSeek={(seconds) => { if (isReady()) onSeek?.(seconds); }}
          onProgress={(progress) => { if (isReady()) onProgress?.(progress); }}
          onDuration={(duration) => { if (isReady()) onDuration?.(duration); }}
          playbackRate={playbackRate}
          volume={volume}
          progressInterval={200}
          onError={(error, data) => {
            if (!active.current || data?.fatal === false) return;
            // HLS forwards recoverable errors too; leave those to its own retry
            // policy. Never expose media errors containing signed source URLs.
            failureLatch.current = true;
            clearPlayer();
            onFailure?.();
            setFailed(true);
          }}
        />
        {url ? children : <div className="video-empty" role="status"><span className="eyebrow">VIDEO WORKSPACE</span><h2>準備好，開始記錄下一球</h2><p>先選擇影片來源並填妥資料，再連結影片。</p><span>影片載入後，配對記錄軟體即可在好球帶標記。</span></div>}
      </div>
    </>
  );
};

// Each source owns selection/signing. The shared frame owns playback recovery.
export const BasePlayer = ({ url, sourceVersion = 0, videoInput, sourceTarget, onRetry, ...props }) => {
  const [retry, setRetry] = useState(0);
  return (
    <div className="player-frame">
      {sourceTarget ? createPortal(<div className="video-input">{videoInput}</div>, sourceTarget) : <div className="video-input">{videoInput}</div>}
      <PlaybackSession
        key={`${url}:${sourceVersion}:${retry}`}
        {...props}
        url={url}
        onRetry={onRetry || (() => setRetry((version) => version + 1))}
      />
    </div>
  );
};
